import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Progress,
  Table,
  Tooltip,
  message,
} from '@surecompanies/core_components'
import type { TableProps } from '@surecompanies/core_components'
import type { SorterResult } from 'antd/es/table/interface'
import CloseOutlined from '@ant-design/icons/CloseOutlined'
import DownOutlined from '@ant-design/icons/DownOutlined'
import LoadingOutlined from '@ant-design/icons/LoadingOutlined'
import RightOutlined from '@ant-design/icons/RightOutlined'

import { useQueryClient } from '@tanstack/react-query'
import { convertToLocalFromUTCISODate } from 'helpers/datetime'
import { useSearchParams } from 'hooks/useSearchParams'
import useCarrierTasks from '../hooks/useCarrierTasks'
import type { TaskResponseDto } from '../hooks/useCarrierTasks'
import {
  NO_PLANS_AVAILABLE_MESSAGE,
  PlanBotTaskStatus,
  PlanBotTriggerSource,
  cancelTask,
  computeTaskProgressPercent,
  downloadCarrierFile,
  formatTaskFailureDetail,
  formatTaskStates,
  isNoPlansAvailableError,
  sumTaskAreas,
  sumTaskProgress,
  type CarrierDownloadType,
  type CarrierTaskSortField,
  type TaskProgress,
} from '../api/fetch-carrier-api'
import { formatTriggerSource, formatTriggeredBy } from '../constants'
import CarrierConfigDrawer from './CarrierConfigDrawer'
import CarrierConnectionDrawer from './CarrierConnectionDrawer'
import DownloadCarrierCsvModal from './DownloadCarrierCsvModal'
import FetchCarrierModal from './FetchCarrierModal'
import TaskStateSummaryTable from './TaskStateSummaryTable'
import styles from '../PlanManagementV3.module.scss'

const CANCELLABLE_STATUSES = new Set<PlanBotTaskStatus>([
  PlanBotTaskStatus.QUEUED,
  PlanBotTaskStatus.IN_PROGRESS,
])

/** @internal exported for unit tests */
export function isFetchCarrierRowExpandable(row: TaskResponseDto): boolean {
  if (
    CANCELLABLE_STATUSES.has(row.status) ||
    row.status === PlanBotTaskStatus.FAILED ||
    row.status === PlanBotTaskStatus.CANCELLED
  ) {
    return false
  }
  return (
    (row.state?.length ?? 0) > 0 ||
    Object.keys(row.summaryInfo ?? {}).length > 0
  )
}

const STATUS_PILL_CLASS = {
  success: styles.statusPillSuccess,
  error: styles.statusPillError,
  partial: styles.statusPillPending,
  pending: styles.statusPillPending,
  neutral: styles.statusPillNeutral,
} as const

const PENDING_TONE_COLOR = '#92400e'

function StatusPill({
  label,
  variant,
}: {
  label: string
  variant: keyof typeof STATUS_PILL_CLASS
}) {
  return (
    <span className={`${styles.statusPill} ${STATUS_PILL_CLASS[variant]}`}>
      {label}
    </span>
  )
}

function FetchingStatus({
  percent,
  progress,
  isCancelling,
  onCancel,
}: {
  percent: number
  progress?: TaskProgress | null
  isCancelling?: boolean
  onCancel?: () => void
}) {
  const totals = sumTaskProgress(progress)
  // Count completed, inProgress, AND failed items as "started" so the
  // progress bar moves as soon as a ZIP is picked up (rather than waiting
  // for the backend's batch-completion flush, which can batch ~100 ZIPs at
  // a time, leaving the bar stuck at 0% for small jobs) and doesn't move
  // backward once an in-progress ZIP is later flushed as failed.
  const started = totals
    ? totals.completed + totals.inProgress + totals.failed
    : 0
  const circlePercent =
    totals && totals.total > 0
      ? Math.round((started / totals.total) * 100)
      : percent
  const circle = (
    <Progress
      type="circle"
      percent={circlePercent}
      size={16}
      showInfo={false}
      strokeWidth={14}
      strokeColor={PENDING_TONE_COLOR}
      trailColor="#f1f2f4"
    />
  )
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {progress ? (
          <Tooltip title={formatProgressTooltip(progress)}>{circle}</Tooltip>
        ) : (
          circle
        )}
        <span className={styles.statusFetchingLabel}>Fetching</span>
        {onCancel && (
          <Tooltip title="Cancel task">
            <span
              role="button"
              tabIndex={0}
              onClick={onCancel}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onCancel()
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                cursor: 'pointer',
                color: '#475467',
                marginLeft: 2,
              }}
            >
              {isCancelling ? (
                <LoadingOutlined style={{ fontSize: 12 }} />
              ) : (
                <CloseOutlined style={{ fontSize: 12 }} />
              )}
            </span>
          </Tooltip>
        )}
      </span>
      {totals && (
        <span className={styles.statusFetchingCount}>
          {started.toLocaleString()}/
          <span className={styles.statusFetchingCountCompleted}>
            {totals.total.toLocaleString()}
          </span>{' '}
          zips
        </span>
      )}
    </span>
  )
}

const SCAN_PASS_LABELS: Record<keyof TaskProgress, string> = {
  broadScan: 'Broad scan',
  standardScan: 'Standard scan',
  deepScan: 'Deep scan',
}

function formatProgressTooltip(progress: TaskProgress): React.ReactNode {
  const passes = (Object.keys(SCAN_PASS_LABELS) as (keyof TaskProgress)[])
    .map((key) => ({ key, label: SCAN_PASS_LABELS[key], ...progress[key] }))
    .filter((pass) => pass.total > 0)

  if (passes.length === 0) return null

  return (
    <div>
      {passes.map((pass) => {
        const detail: string[] = []
        if (pass.inProgress > 0) detail.push(`${pass.inProgress} processing`)
        if (pass.failed > 0) detail.push(`${pass.failed} failed`)
        return (
          <div key={pass.key}>
            {pass.label}: {pass.completed + pass.inProgress + pass.failed}/
            {pass.total}
            {detail.length > 0 ? ` (${detail.join(', ')})` : ''}
          </div>
        )
      })}
    </div>
  )
}

function formatPartialTooltip(row: TaskResponseDto): string {
  const detail = formatTaskFailureDetail(row.summaryInfo, row.errorMessage)
  if (detail) return detail
  return row.status === PlanBotTaskStatus.PARTIAL
    ? 'Task partially completed — no details available'
    : 'Task failed — no details available'
}

function renderStatusTag(
  row: TaskResponseDto,
  options?: { isCancelling?: boolean; onCancel?: () => void }
) {
  switch (row.status) {
    case PlanBotTaskStatus.QUEUED:
      return (
        <FetchingStatus
          percent={computeTaskProgressPercent(row.progress) ?? 0}
          progress={row.progress}
          isCancelling={options?.isCancelling}
          onCancel={options?.onCancel}
        />
      )
    case PlanBotTaskStatus.IN_PROGRESS: {
      // Prefer the API-reported three-pass progress. Some carrier tasks
      // (e.g. simple CARRIER_API integrations, or pre-v2 tasks) don't report
      // it, so fall back to the legacy summaryInfo-based calculation.
      let percent = computeTaskProgressPercent(row.progress)
      if (percent === null) {
        const { totalAreas, completedAreas } = sumTaskAreas(row.summaryInfo)
        percent =
          totalAreas > 0 ? Math.round((completedAreas / totalAreas) * 100) : 0
      }
      return (
        <FetchingStatus
          percent={percent}
          progress={row.progress}
          isCancelling={options?.isCancelling}
          onCancel={options?.onCancel}
        />
      )
    }
    case PlanBotTaskStatus.COMPLETED:
      return <StatusPill label="Completed" variant="success" />
    case PlanBotTaskStatus.FAILED:
      return (
        <Tooltip title={formatPartialTooltip(row)}>
          <StatusPill label="Failed" variant="error" />
        </Tooltip>
      )
    case PlanBotTaskStatus.PARTIAL:
      return (
        <Tooltip title={formatPartialTooltip(row)}>
          <StatusPill label="Partial" variant="partial" />
        </Tooltip>
      )
    case PlanBotTaskStatus.CANCELLED:
      return <StatusPill label="Cancelled" variant="neutral" />
    default:
      return <StatusPill label={row.status} variant="neutral" />
  }
}

export default function FetchCarrierTab() {
  const queryClient = useQueryClient()
  const { params: searchParams, setParams } = useSearchParams<{
    tab?: string
    action?: string
    carrier?: string
    taskId?: string
    /** Legacy alias for taskId — accepted for backward-compat with older deeplinks */
    task?: string
    dataType?: string
    state?: string
  }>()
  // Intentional: the connection drawer is a dev-only testing surface, reachable by
  // manually appending ?action=live to the URL. It is not linked from the UI on purpose.
  const isConnectionDrawerOpen = searchParams.action === 'live'
  // The carrier config drawer is reachable only via ?action=bot-setting; there is
  // no button in the UI to open it.
  const isConfigDrawerOpen = searchParams.action === 'bot-setting'
  // The download modal is opened via ?action=download (used by the "Download" button).
  // The modal always requires the user to click Download.
  const isDownloadModalOpen = searchParams.action === 'download'

  // Auto-download (no modal) is triggered by ?action=direct-download — intended for
  // plan-bot deeplinks. Accepts both `taskId=` and the legacy `task=` alias.
  const autoTaskId = searchParams.taskId ?? searchParams.task
  const autoDataType = searchParams.dataType as CarrierDownloadType | undefined
  const shouldAutoDownload =
    searchParams.action === 'direct-download' && !!autoTaskId && !!autoDataType

  // Guard against React StrictMode double-invoke and re-entrancy.
  const autoDownloadFiredRef = useRef(false)

  useEffect(() => {
    if (!shouldAutoDownload || autoDownloadFiredRef.current) return
    autoDownloadFiredRef.current = true

    let unmounted = false

    const taskId = autoTaskId
    const downloadType = autoDataType
    if (!taskId || !downloadType) return

    const hide = message.loading('Downloading…', 0)
    downloadCarrierFile({
      taskId,
      downloadType,
      state: searchParams.state,
    })
      .then(() => {
        hide()
        message.success('Download complete.')
      })
      .catch((err) => {
        hide()
        if (isNoPlansAvailableError(err)) {
          message.warning(NO_PLANS_AVAILABLE_MESSAGE)
          return
        }
        console.error('Auto-download failed', err)
        message.error('Download failed. Please try again.')
      })
      .finally(() => {
        if (!unmounted) {
          setParams(
            {
              action: undefined,
              carrier: undefined,
              taskId: undefined,
              task: undefined,
              dataType: undefined,
              state: undefined,
            },
            { replace: true }
          )
        }
        autoDownloadFiredRef.current = false
      })

    return () => {
      unmounted = true
    }
  }, [shouldAutoDownload])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [liveGroupCode, setLiveGroupCode] = useState<string | undefined>(
    undefined
  )

  const downloadInitialValues = useMemo(
    () =>
      isDownloadModalOpen
        ? {
            groupCode: searchParams.carrier,
            taskId: searchParams.taskId ?? searchParams.task,
            downloadType: searchParams.dataType as
              | CarrierDownloadType
              | undefined,
            state: searchParams.state,
          }
        : undefined,
    // Only recompute when the modal first opens; deliberately ignore changes to
    // individual filter params so they don't overwrite user selections mid-session.
    [isDownloadModalOpen]
  )

  const handleDownloadModalClose = useCallback(() => {
    setParams(
      {
        action: undefined,
        carrier: undefined,
        taskId: undefined,
        task: undefined,
        dataType: undefined,
        state: undefined,
      },
      { replace: true }
    )
  }, [setParams])

  const handleDownloadModalValuesChange = useCallback(
    (values: {
      groupCode?: string
      taskId?: string
      downloadType?: CarrierDownloadType
      state?: string
    }) => {
      setParams(
        {
          carrier: values.groupCode,
          taskId: values.taskId,
          task: undefined,
          dataType: values.downloadType,
          state: values.state,
        },
        { replace: true }
      )
    },
    [setParams]
  )

  const [cancellingIds, setCancellingIds] = useState<Set<string>>(new Set())
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([])

  const expandedKeySet = useMemo(
    () => new Set(expandedRowKeys),
    [expandedRowKeys]
  )

  const rowClassName = useCallback(
    (record: TaskResponseDto) =>
      expandedKeySet.has(record.id) ? styles.expandedParentRow : '',
    [expandedKeySet]
  )

  const handleExpandedRowsChange = useCallback((keys: readonly React.Key[]) => {
    setExpandedRowKeys([...keys])
  }, [])

  const {
    data,
    total,
    isLoading,
    error,
    page,
    pageSize,
    sort,
    order,
    handlePageChange,
    handleSortChange,
  } = useCarrierTasks()

  useEffect(() => {
    if (error) {
      console.error('Failed to load carrier tasks', error)
      message.error('An error occurred. Please try again.')
    }
  }, [error])

  // Intentionally allows opening the modal even while other carrier fetches
  // are running — multiple carriers can be fetched concurrently.
  const handleOpenFetchModal = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  const handleCancel = useCallback(
    async (row: TaskResponseDto) => {
      setCancellingIds((prev) => new Set(prev).add(row.id))
      try {
        await cancelTask(row.id)
        message.success('Task cancelled.')
        await queryClient.invalidateQueries({ queryKey: ['carrierTasks'] })
      } catch (err) {
        console.error('Failed to cancel task', err)
        message.error('Could not cancel task. Please try again.')
      } finally {
        setCancellingIds((prev) => {
          const next = new Set(prev)
          next.delete(row.id)
          return next
        })
      }
    },
    [queryClient]
  )

  const sortOrder = (field: string) =>
    sort === field
      ? order === 'ASC'
        ? ('ascend' as const)
        : ('descend' as const)
      : null

  const columns = useMemo(
    () => [
      {
        title: 'Time Stamp',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 200,
        sorter: true,
        sortDirections: ['ascend' as const, 'descend' as const],
        showSorterTooltip: false,
        sortOrder: sortOrder('createdAt'),
        render: (value: string, row: TaskResponseDto) =>
          value ? (
            <Tooltip
              title={`ID: ${row.id}`}
              placement="topLeft"
              overlayStyle={{ maxWidth: 'none', whiteSpace: 'nowrap' }}
            >
              <span>{convertToLocalFromUTCISODate(value)}</span>
            </Tooltip>
          ) : (
            ''
          ),
      },
      {
        title: 'Plan Year',
        dataIndex: 'year',
        key: 'year',
        width: 100,
        render: (value: number | null) => value ?? '—',
      },
      {
        title: 'State',
        dataIndex: 'state',
        key: 'state',
        width: 88,
        sorter: true,
        sortDirections: ['ascend' as const, 'descend' as const],
        showSorterTooltip: false,
        sortOrder: sortOrder('state'),
        render: (value: string[]) => {
          const count = value?.length ?? 0
          const label = formatTaskStates(value)
          return (
            <Tooltip title={label}>
              <span>{count || '—'}</span>
            </Tooltip>
          )
        },
      },
      {
        title: 'Carrier Name',
        dataIndex: 'carrierName',
        key: 'carrierName',
        render: (value: string | null) => value ?? '—',
      },
      {
        title: 'Source',
        dataIndex: 'triggerSource',
        key: 'triggerSource',
        width: 100,
        render: (value: PlanBotTriggerSource | null) =>
          value ? (
            <StatusPill label={formatTriggerSource(value)} variant="neutral" />
          ) : (
            '—'
          ),
      },
      {
        title: 'Admin',
        dataIndex: 'triggeredBy',
        key: 'triggeredBy',
        render: (value: string | null) => formatTriggeredBy(value),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 160,
        sorter: true,
        sortDirections: ['ascend' as const, 'descend' as const],
        showSorterTooltip: false,
        sortOrder: sortOrder('status'),
        render: (_: unknown, row: TaskResponseDto) =>
          renderStatusTag(
            row,
            CANCELLABLE_STATUSES.has(row.status)
              ? {
                  isCancelling: cancellingIds.has(row.id),
                  onCancel: () => void handleCancel(row),
                }
              : undefined
          ),
      },
    ],
    [cancellingIds, handleCancel, sort, order]
  )

  const handleTableChange: TableProps<TaskResponseDto>['onChange'] =
    useCallback(
      (_, __, sorter, extra) => {
        if (extra?.action === 'sort') {
          const s = sorter as SorterResult<TaskResponseDto>
          if (s.field && s.order) {
            handleSortChange(
              s.field as CarrierTaskSortField,
              s.order === 'ascend' ? 'ASC' : 'DESC'
            )
          } else {
            handleSortChange('createdAt', 'DESC')
          }
        }
      },
      [handleSortChange]
    )

  const isRowExpandable = useCallback(isFetchCarrierRowExpandable, [])

  return (
    <div className={styles.card}>
      <FetchCarrierModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => setIsModalOpen(false)}
      />
      <DownloadCarrierCsvModal
        open={isDownloadModalOpen}
        initialValues={downloadInitialValues}
        onValuesChange={handleDownloadModalValuesChange}
        onClose={handleDownloadModalClose}
      />
      <CarrierConfigDrawer
        open={isConfigDrawerOpen}
        onClose={() => setParams({ action: undefined }, { replace: true })}
        onLive={(groupCode) => {
          setLiveGroupCode(groupCode)
          setParams({ action: 'live' }, { replace: true })
        }}
      />
      <CarrierConnectionDrawer
        open={isConnectionDrawerOpen}
        defaultGroupCode={liveGroupCode}
        onClose={() => setParams({ action: undefined }, { replace: true })}
      />

      <div className={styles.cardHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h1 className={styles.cardHeaderTitle}>Fetch Carrier</h1>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button onClick={handleOpenFetchModal}>Fetch</Button>
          <Button
            type="primary"
            onClick={() => setParams({ action: 'download' }, { replace: true })}
          >
            Download
          </Button>
        </div>
      </div>

      <div className={styles.fetchCarrierTableCard}>
        <Table
          className={`${styles.flatTable} ${styles.fetchCarrierTable}`}
          dataSource={data}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          onChange={handleTableChange}
          indentSize={0}
          rowClassName={rowClassName}
          expandable={{
            expandedRowKeys,
            onExpandedRowsChange: handleExpandedRowsChange,
            rowExpandable: isRowExpandable,
            expandedRowClassName: () => styles.expandedRow,
            expandedRowRender: (row: TaskResponseDto) => (
              <TaskStateSummaryTable
                taskId={row.id}
                states={row.state}
                summaryInfo={row.summaryInfo}
                taskStatus={row.status}
              />
            ),
            expandIcon: ({ expanded, onExpand, record, expandable }) => {
              if (!expandable) {
                return <span className={styles.expandIconSpacer} />
              }
              return (
                <button
                  type="button"
                  className={styles.expandButton}
                  aria-label={
                    expanded ? 'Collapse state summary' : 'Expand state summary'
                  }
                  aria-expanded={expanded}
                  onClick={(event) => onExpand(record, event)}
                >
                  {expanded ? (
                    <DownOutlined aria-hidden="true" />
                  ) : (
                    <RightOutlined aria-hidden="true" />
                  )}
                </button>
              )
            },
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} items`,
            onChange: handlePageChange,
          }}
          sortDirections={['ascend', 'descend']}
          showSorterTooltip={false}
          locale={{ emptyText: 'No Data' }}
        />
      </div>
    </div>
  )
}
